// ═══════════════════════════════════════════════════════════════
// freight-carrier-page.ts — the carrier's own quote page, as a rule
// ═══════════════════════════════════════════════════════════════
// Drew, 2026-10-20: "maybe we could even have them interact with MOS?"
//
// Every carrier asked on a quote request has a private link,
// /freight-quote/<page_token> (freight_quotes.page_token, 64 hex). The page
// shows the load sheet (_shared/freight-load-sheet.ts) with the pallets'
// photos, and takes their answer: a price per truck or all in, the
// equipment, when they can pick up, the transit time, a note — or just a
// question. Their own numbers land on their own row (read_by 'carrier', no
// reading and no confirming needed), the Freight card wakes with them, and
// the job's log carries the line. Booking stays a person's press.
//
// The token is the whole key: long, random, unique, one per carrier, so a
// link shows one carrier's own row and nobody else's price. Nothing is
// written on a view (a mail scanner opens links too); a post is limited to
// CARRIER_PAGE_DAILY_POSTS a day per link. Once the request is booked,
// cancelled or done the page says so and takes nothing.
//
// Dependency-free: the edge routes (freight-carrier-page-routes.tsx) and
// the page (src/components/freight/carrier/) both import it, and
// `npm run test:freight-board` (section O) runs it under Node.
// ═══════════════════════════════════════════════════════════════

/** A page token: two v4 uuids without their dashes. */
export const CARRIER_PAGE_TOKEN_RE = /^[0-9a-f]{64}$/;

/** The page's address inside the app (App.tsx renders it before sign-in). */
export const CARRIER_PAGE_PREFIX = "/freight-quote/";

/** Posts one link may make in a day. */
export const CARRIER_PAGE_DAILY_POSTS = 30;

/** The largest price a carrier can type (a typo guard, not a business rule). */
export const CARRIER_PRICE_MAX = 500_000;

export function isCarrierPageToken(t: unknown): t is string {
  return typeof t === "string" && CARRIER_PAGE_TOKEN_RE.test(t);
}

/** The full link a carrier opens. */
export function carrierPageUrl(appUrl: string, token: string): string {
  return `${appUrl.replace(/\/+$/, "")}${CARRIER_PAGE_PREFIX}${token}`;
}

/** The token in a page path, or null ("/freight-quote/<64 hex>", a trailing
 *  slash or capitals from a pasted link still land). */
export function carrierPageTokenOf(pathname: string): string | null {
  const m = /^\/freight-quote\/([0-9a-fA-F]{64})\/?$/.exec(pathname);
  return m ? m[1].toLowerCase() : null;
}

/** Where a request stands for the carrier looking at it. */
export type CarrierPageState = "open" | "booked_you" | "booked_other" | "closed";

export function carrierPageState(requestStatus: string | null | undefined, quoteStatus: string | null | undefined): CarrierPageState {
  if (quoteStatus === "booked") return "booked_you";
  if (requestStatus === "quoting" || requestStatus === "draft") return "open";
  if (requestStatus === "booked") return "booked_other";
  return "closed";
}

/** The sentence the page shows when it takes no quote. */
export function carrierPageStateWords(state: CarrierPageState): string | null {
  switch (state) {
    case "booked_you": return "You've got this load. Elyce will be in touch about the pickup.";
    case "booked_other": return "This load has been booked. Thank you for quoting it.";
    case "closed": return "This request is closed. Thank you.";
    default: return null;
  }
}

export interface CarrierQuote {
  perTruck: number | null;
  allIn: number | null;
  equipment: string | null;
  pickupWindow: string | null;
  transit: string | null;
  notes: string | null;
  /** Their name, when they gave it. */
  name: string | null;
  /** A question instead of (or beside) a price. */
  question: string | null;
}

const text = (v: unknown, max: number): string | null => {
  if (typeof v !== "string") return null;
  // No control characters (a tab or line break in a note is kept).
  const t = v.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "").trim();
  return t ? t.slice(0, max) : null;
};

/** "$3,000" / "3000" / "3,000.50" → 3000.5; anything else → null. */
export function priceOf(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v !== "string") return null;
  const t = v.replace(/[$,\s]/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** What a carrier sent, cleaned — or the sentence the page shows. A price is
 *  per truck or all in (one is enough); a question alone is an answer too. */
export function cleanCarrierQuote(body: unknown): { ok: true; quote: CarrierQuote } | { ok: false; error: string } {
  const b = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;
  const rawPer = b.perTruck, rawAll = b.allIn;
  const perTruck = rawPer == null || rawPer === "" ? null : priceOf(rawPer);
  const allIn = rawAll == null || rawAll === "" ? null : priceOf(rawAll);
  if ((rawPer != null && rawPer !== "" && perTruck == null) || (rawAll != null && rawAll !== "" && allIn == null)) {
    return { ok: false, error: "Type the price as a number, like 3000" };
  }
  for (const p of [perTruck, allIn]) {
    if (p != null && !(p > 0 && p <= CARRIER_PRICE_MAX)) return { ok: false, error: "That price does not look right — check the number" };
  }
  const question = text(b.question, 1000);
  if (perTruck == null && allIn == null && !question) {
    return { ok: false, error: "Type your price, or ask your question" };
  }
  return {
    ok: true,
    quote: {
      perTruck, allIn,
      equipment: text(b.equipment, 60),
      pickupWindow: text(b.pickupWindow, 200),
      transit: text(b.transit, 100),
      notes: text(b.notes, 1000),
      name: text(b.name, 100),
      question,
    },
  };
}

/** May this link post today? The day's count carries on, or starts again. */
export function carrierPostAllowed(saves: number | null | undefined, savesOn: string | null | undefined, today: string): { allowed: boolean; saves: number } {
  const n = savesOn === today ? Math.max(0, Number(saves) || 0) : 0;
  return n >= CARRIER_PAGE_DAILY_POSTS ? { allowed: false, saves: n } : { allowed: true, saves: n + 1 };
}

const money = (n: number) => `$${n.toLocaleString("en-US", { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;

/** "$3,000 per truck · Flatbed · pickup Thu Oct 22 · 2 days in transit" —
 *  the card's wake line and the job log's words. */
export function carrierQuoteWords(q: Pick<CarrierQuote, "perTruck" | "allIn" | "equipment" | "pickupWindow" | "transit">): string {
  return [
    q.perTruck != null ? `${money(q.perTruck)} per truck` : null,
    q.allIn != null ? `${money(q.allIn)} all in` : null,
    q.equipment,
    q.pickupWindow ? `pickup ${q.pickupWindow}` : null,
    q.transit ? `${q.transit} in transit` : null,
  ].filter(Boolean).join(" · ");
}
